import { format, isAfter, isBefore, startOfDay } from "date-fns";
import { Navigate, useMatch, useOutletContext } from "react-router-dom";

import { FilterControl } from "@/components/filter-control";
import { TableControl } from "@/components/table-control";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useGetSessions } from "@/features/sessions/api/get-sessions";
import { SessionList } from "@/features/sessions/components/session-list";
import { SessionListSkeleton } from "@/features/sessions/components/session-list-skeleton";
import { SideCalendarWrapper } from "@/features/sessions/components/side-calendar-wrapper";
import { RoleGroupEnum } from "@classview/shared-types";

import { SessionsPageFilters } from "./sessions";

export function SessionsListPage() {
  const { tableControl, tableFilters } = useOutletContext<{
    tableControl: TableControl;
    tableFilters: FilterControl<SessionsPageFilters>;
  }>();

  const { search } = tableControl;
  const { filters } = tableFilters;

  const isInvitedOnly = Boolean(useMatch("/sessions/my-sessions"));

  const { organisation } = useOrganisation();
  const isSessionGuest = organisation.roleGroup === RoleGroupEnum.SESSION_GUEST;

  const { data, isLoading, isSuccess } = useGetSessions(
    {
      pageSize: 999,
      pageIndex: 0,
      search: search ?? undefined,
      invitedOnly: isInvitedOnly,
      date: format(filters.date, "yyyy-MM"),
      organisationId: organisation.id,
    },
    {
      select: (data) => {
        const selectedDate = new Date(filters.date);
        const today = startOfDay(new Date());
        const selectedDateStart = startOfDay(selectedDate);

        // If selected date is earlier than today, use selected date as cutoff
        // Otherwise, use today as cutoff
        const cutoffDate = isBefore(selectedDateStart, today)
          ? selectedDateStart
          : today;

        return {
          ...data,
          items: data.items.filter((session) => {
            const sessionDate = startOfDay(new Date(session.start));
            return (
              isAfter(sessionDate, cutoffDate) ||
              sessionDate.getTime() === cutoffDate.getTime()
            );
          }),
        };
      },
    },
  );

  const { data: sessionsForMonth, isSuccess: isSessionsForMonthSuccess } =
    useGetSessions({
      pageSize: 99,
      pageIndex: 0,
      search: search ?? undefined,
      invitedOnly: isInvitedOnly,
      date: filters.activeMonth,
      organisationId: organisation.id,
    });

  const listOfDaysWhenSessions = isSessionsForMonthSuccess
    ? sessionsForMonth.items.map((session) => new Date(session.start))
    : [];

  /**
   * Session guest users have no access to all sessions page
   */
  if (isSessionGuest && !isInvitedOnly) {
    return <Navigate to="/sessions/my-sessions" replace />;
  }

  return (
    <SideCalendarWrapper activeDays={listOfDaysWhenSessions}>
      {isLoading && <SessionListSkeleton />}
      {isSuccess && (
        <SessionList sessions={data.items} selectedDate={filters.date} />
      )}
    </SideCalendarWrapper>
  );
}
